/**
 * Flagship report export utility.
 * Uses SheetJS (xlsx) to produce multi-sheet .xlsx files.
 * Per-section functions produce individual .csv downloads.
 *
 * All functions accept the real App.jsx reporting data variables directly.
 */
import * as XLSX from 'xlsx';

// ─── helpers ────────────────────────────────────────────────────────────────

function dateStamp() {
  return new Date().toISOString().slice(0, 10);
}

function styleSheet(ws, colWidths) {
  ws['!cols'] = colWidths.map((w) => ({ wch: w }));
  return ws;
}

function toCsv(rows) {
  return '\uFEFF' + rows
    .map((row) => row.map((cell) => {
      const s = String(cell ?? '');
      return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }).join(','))
    .join('\r\n');
}

function triggerDownload(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── per-sheet builders ──────────────────────────────────────────────────────

function sheetMeta(scopeLabel, timeframeLabel) {
  const rows = [
    ['Vision Analytics Report'],
    [],
    ['Generated', new Date().toLocaleString()],
    ['Scope', scopeLabel || 'All'],
    ['Timeframe', timeframeLabel || '—'],
  ];
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [22, 30]);
}

function sheetSummaryKpis(kpiDeltasFull) {
  const rows = [['Metric', 'Value', 'vs Prior Period', 'Delta %']];
  (kpiDeltasFull || []).forEach((k) => {
    rows.push([
      k.label,
      k.displayValue ?? k.value ?? '',
      k.delta != null ? (k.delta > 0 ? '+' + k.delta : k.delta) : '—',
      k.deltaPct != null ? k.deltaPct + '%' : '—',
    ]);
  });
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [32, 16, 18, 12]);
}

function sheetPipeline(stageVisuals) {
  const rows = [['Stage', 'Candidates', 'Share %']];
  (stageVisuals || []).forEach((s) => rows.push([s.label, s.count, s.share]));
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [22, 14, 12]);
}

function sheetConversionFunnel(conversionFunnel) {
  const rows = [['Stage', 'Count', 'Conversion', 'Dropoff']];
  (conversionFunnel || []).forEach((s) =>
    rows.push([s.label, s.count, s.conversionLabel || '—', s.dropoffLabel || '—'])
  );
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [22, 12, 16, 16]);
}

function sheetSourceQuality(sourceQualityItems) {
  const rows = [['Source', 'Candidates', 'Screening Rate %', 'Hire Rate %']];
  (sourceQualityItems || []).forEach((s) =>
    rows.push([s.label, s.count, Math.round(s.screeningRate), Math.round(s.hireRate)])
  );
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [28, 14, 20, 16]);
}

function sheetSourceRoi(sourceRoiItems) {
  const rows = [['Source', 'Candidates', 'Quality Score', 'Cost / Hire ($)', 'ROI Index']];
  (sourceRoiItems || []).forEach((s) =>
    rows.push([s.label, s.count, s.qualityScore ?? '—', s.costPerHired ?? '—', s.roiIndex ?? '—'])
  );
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [28, 14, 16, 18, 12]);
}

function sheetActivityMix(activityMixItems) {
  const rows = [['Activity Type', 'Count', 'Share %']];
  (activityMixItems || []).forEach((a) => rows.push([a.label, a.count, a.share]));
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [32, 12, 12]);
}

function sheetDailyTrend(activityTrendBuckets) {
  const rows = [['Date', 'Activities']];
  (activityTrendBuckets || []).forEach((b) => rows.push([b.label, b.value]));
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [16, 14]);
}

function sheetRecruiterLeaderboard(recruiterLeaderboard) {
  const rows = [['Recruiter', 'Candidates', 'Hires', 'Fill Rate %']];
  (recruiterLeaderboard || []).forEach((r) =>
    rows.push([r.name, r.candidates, r.hires, r.fillRate ?? '—'])
  );
  return styleSheet(XLSX.utils.aoa_to_sheet(rows), [28, 14, 10, 14]);
}

// ─── main xlsx export ────────────────────────────────────────────────────────

/**
 * Build and download a full multi-sheet .xlsx report.
 *
 * Accepts the real reporting data variables from App.jsx directly.
 */
export function downloadXlsxReport({
  kpiDeltasFull = [],
  stageVisuals = [],
  sourceQualityItems = [],
  sourceRoiItems = [],
  activityTrendBuckets = [],
  activityMixItems = [],
  conversionFunnel = [],
  recruiterLeaderboard = [],
  scopeLabel = 'All',
  timeframeLabel = '—',
} = {}) {
  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, sheetMeta(scopeLabel, timeframeLabel), 'Meta');
  XLSX.utils.book_append_sheet(wb, sheetSummaryKpis(kpiDeltasFull), 'KPI Summary');
  XLSX.utils.book_append_sheet(wb, sheetPipeline(stageVisuals), 'Pipeline');
  XLSX.utils.book_append_sheet(wb, sheetConversionFunnel(conversionFunnel), 'Conversion Funnel');
  XLSX.utils.book_append_sheet(wb, sheetSourceQuality(sourceQualityItems), 'Source Quality');
  XLSX.utils.book_append_sheet(wb, sheetSourceRoi(sourceRoiItems), 'Source ROI');
  XLSX.utils.book_append_sheet(wb, sheetActivityMix(activityMixItems), 'Activity Mix');
  XLSX.utils.book_append_sheet(wb, sheetDailyTrend(activityTrendBuckets), 'Daily Trend');
  XLSX.utils.book_append_sheet(wb, sheetRecruiterLeaderboard(recruiterLeaderboard), 'Recruiter Leaderboard');

  XLSX.writeFile(wb, `vision-report-${scopeLabel.replace(/\s+/g, '-').toLowerCase()}-${dateStamp()}.xlsx`);
}

// ─── per-section CSV downloads ───────────────────────────────────────────────

export function exportPipelineCsv(stageVisuals) {
  const rows = [['Stage', 'Candidates', 'Share %']];
  (stageVisuals || []).forEach((s) => rows.push([s.label, s.count, s.share]));
  triggerDownload(toCsv(rows), `pipeline-${dateStamp()}.csv`);
}

export function exportFunnelCsv(conversionFunnel) {
  const rows = [['Stage', 'Count', 'Conversion', 'Dropoff']];
  (conversionFunnel || []).forEach((s) =>
    rows.push([s.label, s.count, s.conversionLabel || '', s.dropoffLabel || ''])
  );
  triggerDownload(toCsv(rows), `conversion-funnel-${dateStamp()}.csv`);
}

export function exportSourceQualityCsv(sourceQualityItems) {
  const rows = [['Source', 'Candidates', 'Screening Rate %', 'Hire Rate %']];
  (sourceQualityItems || []).forEach((s) =>
    rows.push([s.label, s.count, Math.round(s.screeningRate), Math.round(s.hireRate)])
  );
  triggerDownload(toCsv(rows), `source-quality-${dateStamp()}.csv`);
}

export function exportSourceRoiCsv(sourceRoiItems) {
  const rows = [['Source', 'Candidates', 'Quality Score', 'Cost/Hire ($)', 'ROI Index']];
  (sourceRoiItems || []).forEach((s) =>
    rows.push([s.label, s.count, s.qualityScore ?? '', s.costPerHired ?? '', s.roiIndex ?? ''])
  );
  triggerDownload(toCsv(rows), `source-roi-${dateStamp()}.csv`);
}

export function exportActivityMixCsv(activityMixItems) {
  const rows = [['Activity Type', 'Count', 'Share %']];
  (activityMixItems || []).forEach((a) => rows.push([a.label, a.count, a.share]));
  triggerDownload(toCsv(rows), `activity-mix-${dateStamp()}.csv`);
}

export function exportActivityTrendCsv(activityTrendBuckets) {
  const rows = [['Date', 'Activities']];
  (activityTrendBuckets || []).forEach((b) => rows.push([b.label, b.value]));
  triggerDownload(toCsv(rows), `activity-trend-${dateStamp()}.csv`);
}

export function exportRecruiterLeaderboardCsv(recruiterLeaderboard) {
  const rows = [['Recruiter', 'Candidates', 'Hires', 'Fill Rate %']];
  (recruiterLeaderboard || []).forEach((r) =>
    rows.push([r.name, r.candidates, r.hires, r.fillRate ?? ''])
  );
  triggerDownload(toCsv(rows), `recruiter-leaderboard-${dateStamp()}.csv`);
}

export function exportSummaryCsv(snapshot) {
  const rows = [['Metric', 'Value']];
  (snapshot?.kpiDeltasFull || []).forEach((k) =>
    rows.push([k.label, k.displayValue ?? k.value ?? ''])
  );
  triggerDownload(toCsv(rows), `summary-${dateStamp()}.csv`);
}

export function exportSourceWoWCsv(snapshot) {
  const rows = [['Source', 'This Week', 'Last Week', 'Change']];
  (snapshot?.sourceWoW || snapshot?.sourceQuality || []).forEach((s) =>
    rows.push([s.label, s.thisWeek ?? s.count ?? '', s.lastWeek ?? '', s.change ?? ''])
  );
  triggerDownload(toCsv(rows), `source-wow-${dateStamp()}.csv`);
}

export function exportRecruiterActivityCsv(snapshot) {
  const rows = [['Recruiter', 'Activity', 'Count']];
  (snapshot?.recruiterActivity || []).forEach((r) =>
    rows.push([r.name ?? r.label, r.activity ?? '', r.count ?? r.value ?? ''])
  );
  triggerDownload(toCsv(rows), `recruiter-activity-${dateStamp()}.csv`);
}

export function exportTimelineCsv(snapshot) {
  const rows = [['Date', 'Value']];
  (snapshot?.timeline || []).forEach((t) => rows.push([t.label, t.value]));
  triggerDownload(toCsv(rows), `timeline-${dateStamp()}.csv`);
}
